import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../firebase/config';
import { createCheckoutSession } from '../services/stripeService';
import webDevPackages from '../data/webDevPackages';
import './GuestCheckout.scss';
import { FaArrowLeft } from 'react-icons/fa';

const GuestCheckout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});
  const [packageDetails, setPackageDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Parse the query parameters to get package ID and quantity
    const params = new URLSearchParams(location.search);
    const packageId = parseInt(params.get('packageId'));
    const quantity = parseInt(params.get('quantity')) || 1;
    
    if (packageId) {
      // Find the package details from webDevPackages
      const selectedPackage = webDevPackages.find(pkg => pkg.id === packageId);
      
      if (selectedPackage) {
        setPackageDetails({
          ...selectedPackage,
          quantity,
          totalPrice: selectedPackage.basePrice * quantity
        });
      } else {
        // If package not found, redirect back to packages page
        navigate('/packages');
      }
    } else {
      // If no package ID provided, redirect back to packages page
      navigate('/packages');
    }
  }, [location, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10,15}$/.test(formData.phone.replace(/[^\d]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || !packageDetails) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // First create a user document in fredoRegisteredUsers collection
      const userDoc = await addDoc(collection(db, 'fredoRegisteredUsers'), {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        createdAt: new Date().toISOString(),
        isGuest: true
      });
      
      console.log('Guest user registered with ID:', userDoc.id);
      
      // Create order in Firestore with 'pending' status
      const orderData = {
        userId: userDoc.id,
        packageId: packageDetails.id,
        packageTitle: packageDetails.title,
        quantity: packageDetails.quantity,
        totalPrice: packageDetails.totalPrice,
        status: 'pending',
        priceId: packageDetails.priceID,
        createdAt: new Date().toISOString(),
        userDetails: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone
        }
      };
      
      // Save order to Firestore
      const orderRef = await addDoc(collection(db, 'orders'), orderData);
      console.log('Order created with ID:', orderRef.id);
      
      // Redirect to Stripe checkout
      const result = await createCheckoutSession(
        orderRef.id,
        packageDetails.priceID,
        userDoc.id,
        packageDetails.quantity,
        formData.email,
        true // Pass true for isGuest parameter
      );
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create checkout session');
      }
      
      // Stripe's redirectToCheckout will automatically redirect the user
      // No need to handle redirection manually
    } catch (error) {
      console.error('Error processing checkout:', error);
      setErrors({
        form: 'There was an error processing your order. Please try again.'
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="guest-checkout-page">
      <div className="checkout-container">
        <button className="back-button" onClick={() => navigate('/packages')}>
          <FaArrowLeft /> Back to Packages
        </button>
        
        <h1>Complete Your Purchase</h1>
        
        {packageDetails ? (
          <div className="checkout-content">
            <div className="package-summary">
              <h2>Order Summary</h2>
              <div className="package-details">
                <div className="package-name">{packageDetails.title}</div>
                <div className="package-price">${packageDetails.basePrice}</div>
              </div>
              
              <div className="quantity-row">
                <span>Quantity:</span>
                <span>{packageDetails.quantity}</span>
              </div>
              
              <div className="total-row">
                <span>Total:</span>
                <span className="total-price">${packageDetails.totalPrice}</span>
              </div>
            </div>
            
            <div className="checkout-form-container">
              <h2>Your Information</h2>
              {errors.form && <div className="error-message form-error">{errors.form}</div>}
              
              <form onSubmit={handleSubmit} className="checkout-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <div className="error-message">{errors.name}</div>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                  />
                  {errors.email && <div className="error-message">{errors.email}</div>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                  />
                  {errors.phone && <div className="error-message">{errors.phone}</div>}
                </div>
                
                <button 
                  type="submit" 
                  className="proceed-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Processing...' : 'Proceed to Payment'}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="loading">Loading package details...</div>
        )}
      </div>
    </div>
  );
};

export default GuestCheckout; 